import { useQuery, useReactiveVar } from '@apollo/client'
import {
  customerAccessTokenVar,
  storefrontCustomerAccessTokenVar,
} from '@commerce/client'
import type {
  ContextualizeProduct,
  ContextualizeShopifyProduct,
  Product,
} from '@commerce/types/product'
import {
  getCurrencyCountryCode,
  getCustomerQuery,
  getLanguage,
  getProductsPricesByIds,
  normalizeContextualizeProduct,
} from '@commerce/utils'
import WishlistButton from '@components/arkade/list/WishlistButton'
import { SeoHead } from '@components/common'
import {
  ProductColourWall,
  ProductImage,
  ProductSidebar,
  ProductSlider,
} from '@components/product'
import { Container } from '@components/ui'
import { findBundleInfoFromMetafield } from '@lib/helpers/general'
import { rPDWCPD } from '@lib/products'

import { PrismicDocument } from '@prismicio/types'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'

interface ProductViewProps {
  product: Product
  productSiblings: Product[]
  prismicContent: PrismicDocument | any
}
const ProductView: FC<ProductViewProps> = ({
  product,
  productSiblings,
  prismicContent,
}) => {
  const { locale } = useRouter()
  const router = useRouter()
  const [selectedSibling, setSelectedSibling] = useState<Product>()
  const bundleInfo = findBundleInfoFromMetafield(product.metafields)
  const isBundleProduct = !!bundleInfo

  const customerAccessToken = useReactiveVar(customerAccessTokenVar)
  const storefrontCustomerAccessToken = useReactiveVar(
    storefrontCustomerAccessTokenVar
  )

  // get customer specific pricing data
  const { data: customerData, loading: customerLoading } = useQuery(
    getCustomerQuery,
    {
      context: { locale, customerAccessToken, useCustomerApi: true },
      skip: !(customerAccessToken && customerAccessToken.length > 0),
    }
  )

  const allProductIds = [product.id].concat(productSiblings.map((s) => s.id))

  const companyLocationId =
    customerData?.customer?.companyContacts?.edges?.[0]?.node?.company
      ?.locations?.edges?.[0]?.node?.id

  const { data, loading: customPricesLoading } = useQuery(
    getProductsPricesByIds,
    {
      context: { locale },
      variables: {
        country: String(getCurrencyCountryCode(locale)),
        language: String(getLanguage(locale)),
        buyer: {
          customerAccessToken: storefrontCustomerAccessToken,
          companyLocationId,
        },
        query: allProductIds
          .map((i) => i.replace('gid://shopify/Product/', ''))
          .join(' OR '),
      },
      skip: !(
        storefrontCustomerAccessToken &&
        storefrontCustomerAccessToken.length > 0 &&
        companyLocationId
      ),
    }
  )

  // replace customer pricing data in the product
  let productWithContxualisedPrices: ContextualizeProduct[] = []
  if (!customPricesLoading && data?.products) {
    productWithContxualisedPrices = data?.products?.edges?.map(
      ({ node }: { node: ContextualizeShopifyProduct }) =>
        normalizeContextualizeProduct(node)
    )
    const mainProduct = productWithContxualisedPrices.find(
      (p) => p.id === product.id
    )
    if (mainProduct) {
      product = rPDWCPD(product, mainProduct)
    }

    productSiblings = productSiblings.map((p) => {
      const cp = productWithContxualisedPrices.find((mp) => mp.id === p.id)
      if (cp) p = rPDWCPD(p, cp)
      return p
    })
  }

  return (
    <>
      <div className="w-full bg-ui-light-grey">
        <Container className="w-full max-w-none">
          <div className="grid grid-cols-6 gap-2 pb-15 lg:gap-4 lg:pb-20">
            {product.productType === 'Colour' && (
              <>
                <div className="col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductColourWall colour={product.colourCode} />
                </div>
                <div className="relative col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductSlider images={product.images} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
              </>
            )}
            {isBundleProduct && (
              <>
                <div className="col-span-6 hidden md:col-span-3 md:block lg:col-span-2">
                  <ProductImage
                    img={product.images?.[0] ? product.images[0] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[0]?.altText}
                  </div>
                </div>
                <div className="relative col-span-6 hidden  md:col-span-3 md:block lg:col-span-2">
                  <ProductSlider images={product.images?.slice(1)} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
                <div className="relative col-span-6 block md:col-span-3 md:hidden lg:col-span-2">
                  <ProductSlider images={product.images} />
                  <div className="absolute top-5 right-5 z-10">
                    <WishlistButton product={product} />
                  </div>
                </div>
              </>
            )}
            {product.productType !== 'Colour' && !isBundleProduct && (
              <>
                <div className="col-span-6 hidden md:col-span-3 md:block lg:col-span-2">
                  <ProductImage
                    img={product.images?.[0] ? product.images[0] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[0]?.altText}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3 lg:col-span-2">
                  <ProductImage
                    img={product.images?.[1] ? product.images[1] : undefined}
                  />
                  <div className="body-2 mt-2 min-h-[20px] text-ui-dark-grey line-clamp-1">
                    {product?.images?.[1]?.altText}
                  </div>
                </div>
              </>
            )}

            <div key={router.locale} className="col-span-6 lg:col-span-2">
              <ProductSidebar
                product={product}
                productSiblings={productSiblings}
                prismicContent={prismicContent?.data}
                selectedSibling={selectedSibling}
                setSelectedSibling={setSelectedSibling}
              />
            </div>
          </div>
        </Container>
      </div>

      <SeoHead
        title={product.name}
        description={product.description}
        image={{ url: product.images[0]?.url! }}
      />
    </>
  )
}

export default ProductView
